'use client';

/* eslint-disable react-refresh/only-export-components */

import { Badge } from '@/components/ui/badge';
import { darken, hexWithAlpha } from '@/lib/colors';
import { cn } from '@/lib/utils';
import type { FaturaStatus } from '@/lib/transactions/types';

/**
 * Display vocabulary for invoices: Aberta · Fechada · Paga.
 * Pluggy's `overdue` rolls into `Fechada` silently — the bucket is "fechada e
 * em aberto", which encompasses both not-yet-due and past-due closed faturas.
 */
export type InvoiceDisplayStatus = 'aberta' | 'fechada' | 'paga';

export function displayStatusOf(status: FaturaStatus): InvoiceDisplayStatus {
  if (status === 'paid') return 'paga';
  if (status === 'open') return 'aberta';
  return 'fechada'; // closed + overdue
}

const LABELS: Record<InvoiceDisplayStatus, string> = {
  aberta: 'Aberta',
  fechada: 'Fechada',
  paga: 'Paga',
};

const FALLBACK_VARIANTS: Record<InvoiceDisplayStatus, 'info' | 'warning' | 'success'> = {
  aberta: 'info',
  fechada: 'warning',
  paga: 'success',
};

interface Props {
  status: FaturaStatus;
  /**
   * When provided, the badge is tinted from this brand/item color instead of
   * the semantic palette. Three intensities:
   *  - aberta:  outlined (12% bg) + darkened text
   *  - fechada: filled (24% bg) + darkened text
   *  - paga:    soft (8% bg) + muted text — visually "done"
   */
  accentColor?: string | null;
  size?: 'default' | 'sm';
  className?: string;
}

const ACCENT_RECIPES: Record<
  InvoiceDisplayStatus,
  { bgAlpha: number; textDarken: number; border?: number }
> = {
  aberta: { bgAlpha: 0.12, textDarken: 0.30, border: 0.20 },
  fechada: { bgAlpha: 0.24, textDarken: 0.40 },
  paga: { bgAlpha: 0.08, textDarken: 0.10 },
};

export function FaturaStatusBadge({ status, accentColor, size = 'sm', className }: Props) {
  const d = displayStatusOf(status);

  if (accentColor) {
    const recipe = ACCENT_RECIPES[d];
    const style: React.CSSProperties = {
      backgroundColor: hexWithAlpha(accentColor, recipe.bgAlpha),
      color: darken(accentColor, recipe.textDarken),
    };
    if (recipe.border !== undefined) {
      style.boxShadow = `inset 0 0 0 1px ${hexWithAlpha(accentColor, recipe.border)}`;
    }
    return (
      <Badge
        size={size}
        className={cn('border-transparent', className)}
        style={style}
      >
        {LABELS[d]}
      </Badge>
    );
  }

  return (
    <Badge variant={FALLBACK_VARIANTS[d]} size={size} className={className}>
      {LABELS[d]}
    </Badge>
  );
}
